import {create} from 'zustand';
import AsyncStorage from '@react-native-async-storage/async-storage';

/**
 * Whether the desktop rail is showing labels or only glyphs.
 *
 * A console is a thing people arrange once and then live in, so the choice is
 * persisted — the same reason the console writes it to `localStorage` under
 * `samvision-sidebar-collapsed`. AsyncStorage is the cabinet's equivalent and is shimmed
 * onto `localStorage` in the web build, so the two products end up storing the same
 * preference the same way.
 *
 * A failed write costs the choice on the NEXT launch, not this one; refusing to collapse
 * because storage is full would cost it immediately.
 */
const STORAGE_KEY = 'samvision.rail-collapsed';

type RailState = {
  collapsed: boolean;
  hydrated: boolean;
  toggle: () => void;
  hydrate: () => Promise<void>;
};

export const useRailStore = create<RailState>()((set, get) => ({
  collapsed: false,
  hydrated: false,

  toggle: () => {
    const next = !get().collapsed;
    set({collapsed: next});
    void AsyncStorage.setItem(STORAGE_KEY, String(next)).catch(() => {});
  },

  hydrate: async () => {
    try {
      const stored = await AsyncStorage.getItem(STORAGE_KEY);
      if (stored === 'true' || stored === 'false') {
        set({collapsed: stored === 'true'});
      }
    } catch {
      // Unreadable storage means the rail opens expanded. Nothing to report.
    } finally {
      set({hydrated: true});
    }
  },
}));
